/**
 * Bildmarke fuer AudIT.
 *
 * Entwurfsgedanke: Die Marke soll neben dem Krannich-KI-Logo bestehen, also
 * dessen Bildsprache aufnehmen – gelbe Kreisscheibe, schwarze Glyphe, feines
 * Netz aus Knoten und Linien. Inhaltlich steht sie fuer das, was AudIT tut:
 * ein Haken als Zeichen der Pruefung, umgeben von den Quellen, die geprueft
 * werden. Die Knoten sind bewusst unregelmaessig verteilt; ein symmetrisches
 * Muster haette wie ein Ladezeichen gewirkt.
 *
 * Zwei Groessenstufen, weil ein Netz bei 24 px zu Brei wird:
 *  * "mark"  – nur Scheibe und Haken, fuer Kopfzeile und Favicon.
 *  * "full"  – mit Netz, fuer Anmeldeseite und Druckstuecke.
 *
 * Farben sind fest und nicht an Designtoken gebunden: Eine Marke aendert ihre
 * Farbe nicht mit dem Farbschema. Lediglich der Haken folgt der Scheibe, damit
 * er auf dunklem Grund nicht verschwindet.
 */
import { KRANNICH_BLACK, KRANNICH_YELLOW } from '../lib/brand.js';

export interface AuditLogoProps {
  /** Kantenlaenge in Pixeln. */
  size?: number;
  variant?: 'mark' | 'full';
  /** Wird als zugaengliche Bezeichnung gesetzt; ohne Wert rein dekorativ. */
  title?: string;
  className?: string;
}

/** Knoten des Netzes: Position und Ankerpunkt auf dem Kreisrand. */
const NODES: Array<{ x: number; y: number; ax: number; ay: number; r: number }> = [
  { x: 6, y: 10, ax: 12.2, ay: 14.8, r: 2.4 },
  { x: 42, y: 12, ax: 36.5, ay: 15.7, r: 2.0 },
  { x: 8, y: 40, ax: 13.4, ay: 34.6, r: 2.0 },
  { x: 40, y: 38, ax: 35.3, ay: 33.9, r: 2.4 },
];

export function AuditLogo({ size = 32, variant = 'mark', title, className }: AuditLogoProps) {
  const decorative = title === undefined;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      className={className}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={title}
      focusable="false"
    >
      {title !== undefined ? <title>{title}</title> : null}

      {variant === 'full' ? (
        <g>
          {/* Netz zwischen den Knoten – zurueckhaltend, damit die Scheibe fuehrt. */}
          <g stroke={KRANNICH_YELLOW} strokeWidth={1.1} fill="none" opacity={0.55}>
            <path d="M6 10 L42 12" />
            <path d="M6 10 L8 40" />
            <path d="M42 12 L40 38" />
            <path d="M8 40 L40 38" />
          </g>
          {/* Speichen zur Scheibe: die Quellen, die zusammenlaufen. */}
          <g stroke={KRANNICH_YELLOW} strokeWidth={1.6} fill="none">
            {NODES.map((n) => (
              <path key={`${n.x}-${n.y}`} d={`M${n.x} ${n.y} L${n.ax} ${n.ay}`} />
            ))}
          </g>
          <g fill={KRANNICH_BLACK}>
            {NODES.map((n) => (
              <circle key={`${n.x}-${n.y}`} cx={n.x} cy={n.y} r={n.r} />
            ))}
          </g>
        </g>
      ) : null}

      <circle cx={24} cy={24} r={15} fill={KRANNICH_YELLOW} />
      <path
        d="M17.2 24.6 L22 29.3 L31.2 19.4"
        fill="none"
        stroke={KRANNICH_BLACK}
        strokeWidth={4.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Wortmarke. "Aud" mager, "IT" fett – die Doppeldeutigkeit des Namens wird
 * dadurch lesbar, ohne sie zu erklaeren.
 */
export function AuditWordmark({ className }: { className?: string }) {
  return (
    <span className={className}>
      Aud<strong>IT</strong>
    </span>
  );
}
